/**
 * Booking Page for Cal.com Integration
 * Handles province-based routing to Cal.com event types
 * Calendar + time-slot picker for a user-friendly booking flow.
 */

import { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { createBookingProvider, executeSuccessActions } from '@marketing-and-funnels/funnel-template';
import { useFunnelConfig } from '@/hooks/use-funnel-config';
import { CheckCircle2, Loader2, CalendarDays, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

function formatDateKey(d: Date): string {
  return d.toISOString().slice(0, 10);
}

// Province → IANA timezone and short label for "times in X local time"
const PROVINCE_TIMEZONE: Record<string, { zone: string; label: string }> = {
  AB: { zone: 'America/Edmonton', label: 'Alberta (Mountain Time)' },
  BC: { zone: 'America/Vancouver', label: 'British Columbia (Pacific Time)' },
  MB: { zone: 'America/Winnipeg', label: 'Manitoba (Central Time)' },
  NB: { zone: 'America/Moncton', label: 'New Brunswick (Atlantic Time)' },
  NL: { zone: 'America/St_Johns', label: 'Newfoundland and Labrador (Newfoundland Time)' },
  NS: { zone: 'America/Halifax', label: 'Nova Scotia (Atlantic Time)' },
  NT: { zone: 'America/Yellowknife', label: 'Northwest Territories (Mountain Time)' },
  NU: { zone: 'America/Iqaluit', label: 'Nunavut (Eastern Time)' },
  ON: { zone: 'America/Toronto', label: 'Ontario (Eastern Time)' },
  PE: { zone: 'America/Halifax', label: 'Prince Edward Island (Atlantic Time)' },
  QC: { zone: 'America/Toronto', label: 'Quebec (Eastern Time)' },
  SK: { zone: 'America/Regina', label: 'Saskatchewan (Central Time)' },
  YT: { zone: 'America/Whitehorse', label: 'Yukon (Pacific Time)' },
};

const Booking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const config = useFunnelConfig();
  const { toast } = useToast();
  
  const formData = location.state?.formData || {};
  const contactId = location.state?.contactId;
  const province = location.state?.province || formData.province;
  
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(false);

  const bookingProvider = createBookingProvider(config.booking);

  // Group slots by date for calendar + time picker
  const { slotsByDate, availableDates } = useMemo(() => {
    const byDate: Record<string, typeof availableSlots> = {};
    for (const slot of availableSlots) {
      const key = slot.date;
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(slot);
    }
    for (const arr of Object.values(byDate)) {
      arr.sort((a, b) => a.time.getTime() - b.time.getTime());
    }
    return {
      slotsByDate: byDate,
      availableDates: Object.keys(byDate).sort(),
    };
  }, [availableSlots]);

  const slotsForSelectedDate = selectedDate ? slotsByDate[formatDateKey(selectedDate)] ?? [] : [];

  const provinceTz = province ? PROVINCE_TIMEZONE[province] : PROVINCE_TIMEZONE['ON'];
  const slotTimeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: provinceTz.zone,
  };

  useEffect(() => {
    if (!formData.firstName || !formData.email) {
      // Redirect if no form data
      navigate('/');
      return;
    }
    
    // Load available slots
    loadAvailableSlots();
  }, [province]);

  const loadAvailableSlots = async () => {
    if (config.booking.type !== 'calcom') return;
    
    setLoading(true);
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() + 1); // Start from tomorrow
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30); // Next 30 days
      
      const slots = await bookingProvider.getAvailableSlots(
        startDate.getTime(),
        endDate.getTime(),
        'America/Toronto', // Default to Eastern time
        province
      );
      
      // Transform slots to a usable format
      // Cal.com v2 returns { "YYYY-MM-DD": [{ start: "ISO8601" }, ...] }; other APIs may use startTime or raw string
      const formattedSlots: any[] = [];
      for (const [date, times] of Object.entries(slots.freeSlots)) {
        if (Array.isArray(times)) {
          times.forEach((time) => {
            const timeStr = typeof time === 'string' ? time : (time as any).start ?? (time as any).startTime;
            const slotTime = new Date(timeStr);
            if (Number.isNaN(slotTime.getTime())) return; // skip invalid dates
            formattedSlots.push({
              date: date,
              time: slotTime,
              displayTime: slotTime.toLocaleString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                timeZone: 'America/Toronto',
              }),
            });
          });
        }
      }
      
      // Sort by time
      formattedSlots.sort((a, b) => a.time.getTime() - b.time.getTime());
      setAvailableSlots(formattedSlots);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to load available slots',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast({
        title: 'Error',
        description: 'Please select a date and time',
        variant: 'destructive',
      });
      return;
    }

    setBooking(true);
    try {
      const appointmentDate = new Date(selectedTime).toISOString();
      
      const result = await bookingProvider.bookAppointment({
        contactId,
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        appointmentDate,
        timezone: 'America/Toronto',
        notes: `Province: ${province}\nQualification: ${formData.qualification}`,
        quizData: {
          province,
          ...formData,
        },
      });

      if (result.success) {
        // Execute success actions
        await executeSuccessActions(config.successActions, {
          contactId,
          bookingId: result.bookingId,
          appointmentId: result.appointmentId,
          formData,
          bookingData: result,
          ghlConfig: config.ghl,
        });

        // Navigate to confirmed booking page
        navigate('/confirmed-booking', {
          state: {
            formData,
            contactId,
            bookingId: result.bookingId,
            appointmentId: result.appointmentId,
          },
        });
      } else {
        throw new Error(result.error || 'Booking failed');
      }
    } catch (error: any) {
      toast({
        title: 'Booking Error',
        description: error.message || 'Failed to book appointment',
        variant: 'destructive',
      });
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-amber-400" />
          <p>Loading available time slots...</p>
        </div>
      </div>
    );
  }

  const calendarClassNames = {
    caption_label: 'text-sm font-medium text-slate-200',
    head_cell: 'text-slate-500 rounded-md w-9 font-normal text-[0.8rem]',
    day: 'h-9 w-9 p-0 font-normal text-slate-300 hover:bg-slate-700 hover:text-white rounded-md',
    day_selected: 'bg-amber-500 text-slate-900 hover:bg-amber-500 focus:bg-amber-500',
    day_today: 'bg-slate-700 text-amber-400 border border-amber-500/50',
    day_outside: 'text-slate-600 opacity-50',
    day_disabled: 'text-slate-600 opacity-40 cursor-not-allowed',
    nav_button_previous: 'absolute left-1 text-slate-400 hover:text-white',
    nav_button_next: 'absolute right-1 text-slate-400 hover:text-white',
  };

  return (
    <div className="min-h-screen bg-slate-900 py-12">
      <div className="container mx-auto px-4 max-w-4xl">
        <Card className="p-8 bg-slate-800/80 border-slate-700 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-6 text-white">Schedule Your Consultation</h1>
          <p className="text-slate-400 mb-8">
            Select a date and time that works for you. We'll send you a confirmation email.
          </p>

          {province && (
            <div className="mb-6 p-4 bg-slate-700/50 border border-slate-600 rounded-lg">
              <p className="text-sm text-slate-300">
                <strong className="text-amber-400">Province:</strong> {province}
                {config.booking.type === 'calcom' && (config.booking as any).provinceEventTypeMap?.[province] && (
                  <span className="text-slate-500 ml-2">(Province-specific calendar)</span>
                )}
              </p>
            </div>
          )}

          {availableSlots.length === 0 ? (
            <p className="text-slate-400 py-8">No available slots found. Please try again later.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Calendar: pick a date */}
              <div className="space-y-3">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-slate-200">
                  <CalendarDays className="h-5 w-5 text-amber-400" />
                  Choose a date
                </h2>
                <Card className="p-4 inline-block bg-slate-800 border-slate-700">
                  <Calendar
                    mode="single"
                    selected={selectedDate ?? undefined}
                    onSelect={(d) => {
                      setSelectedDate(d ?? null);
                      setSelectedTime(null);
                    }}
                    disabled={(date) => {
                      const key = formatDateKey(date);
                      return !availableDates.includes(key);
                    }}
                    fromDate={availableDates[0] ? new Date(availableDates[0]) : new Date()}
                    toDate={
                      availableDates.length
                        ? new Date(availableDates[availableDates.length - 1])
                        : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                    }
                    className="rounded-md border-0"
                    classNames={calendarClassNames}
                  />
                </Card>
              </div>

              {/* Time slots for selected date */}
              <div className="space-y-3">
                <h2 className="text-lg font-semibold flex items-center gap-2 text-slate-200">
                  <Clock className="h-5 w-5 text-amber-400" />
                  {selectedDate
                    ? `Available times for ${selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}`
                    : 'Choose a date first'}
                </h2>
                {selectedDate && (
                  <p className="text-slate-500 text-sm -mt-1">
                    Times shown in {provinceTz.label}.
                  </p>
                )}
                {!selectedDate ? (
                  <p className="text-slate-500 text-sm py-4">Select a date on the calendar to see available times.</p>
                ) : slotsForSelectedDate.length === 0 ? (
                  <p className="text-slate-500 text-sm py-4">No times available for this date.</p>
                ) : (
                  <div className="h-[280px] overflow-y-auto rounded-lg border border-slate-600 bg-slate-800/50 p-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {slotsForSelectedDate.map((slot, index) => {
                        const timeIso =
                          slot.time?.getTime && !Number.isNaN(slot.time.getTime()) ? slot.time.toISOString() : '';
                        const isSelected = selectedTime === timeIso;
                        return (
                          <button
                            key={index}
                            type="button"
                            onClick={() => (timeIso ? setSelectedTime(timeIso) : null)}
                            className={`rounded-lg border px-4 py-3 text-left text-sm font-medium transition-colors ${
                              isSelected
                                ? 'border-amber-500 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30'
                                : 'border-slate-600 bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white'
                            }`}
                          >
                            {slot.time.toLocaleTimeString('en-US', slotTimeOptions)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedTime && selectedDate && (
            <div className="mb-6 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
              <p className="text-sm text-slate-400 mb-1">Your consultation</p>
              <p className="font-semibold text-amber-400">
                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                {' at '}
                {new Date(selectedTime).toLocaleTimeString('en-US', slotTimeOptions)}
              </p>
              <div className="mt-3 pt-3 border-t border-amber-500/20 text-sm text-slate-300 space-y-1">
                {formData.firstName && (
                  <p><span className="text-slate-500">Name:</span> {[formData.firstName, formData.lastName].filter(Boolean).join(' ')}</p>
                )}
                {formData.email && (
                  <p><span className="text-slate-500">Email:</span> {formData.email}</p>
                )}
                {formData.phone && (
                  <p><span className="text-slate-500">Phone:</span> {formData.phone}</p>
                )}
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              onClick={handleBooking}
              disabled={!selectedTime || booking}
              className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
            >
              {booking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Confirm Booking
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white"
            >
              Cancel
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Booking;
